<template>
  <div>

    <approval-remarks :row-data="actionData" :steps="actionData.steps" :legal-step="3" />

    <div v-if="legalApproved">
      <section v-if="isTowerCo()">

        <!-- XOL-596 (approval lock; Carlos, 2026-10-14: "Move the lock to the top of the
             section, aligned to the right"): this section has no collapse header, so the
             line sits at its top, right-aligned. -->
        <approval-lock-line
          ref="lockLine"
          placement="end"
          :row="step"
          :title="step.stepName"
          :decided="approvalDecided(step)"
          :frozen="frozen"
          :can-lock="canLockApproval(step) && !readOnly && !frozen"
          :can-unlock="canUnlockApproval(step) && !readOnly && !frozen"
          :busy="lockRunning !== null"
          :busy-label="lockBusyLabel()"
          :format="formatDateTime"
          text-id="step5-approval-lock-line"
          @lock="lockApproval(step)"
          @unlock="unlockApproval(step)" />

        <el-form label-position="top" :inline="true">

          <el-form-item label="Approval">
            <!-- XOL-596 (UX 15): the final approval is for an approver or a
                 manager with access to requests. The select stays visible, showing
                 the stored decision, but is disabled for anyone else, with the
                 reason below it. -->
            <!-- XOL-596 (decision fields): the status and the date are FIELDS; the C-12
                 card carries Pending, Saving and Saved. -->
            <el-select v-model="step.approvedStatus"
            @change="approval()"
            placeholder="Select"
            :disabled="disabled || !canDecideFinal || locked || frozen"
            :aria-describedby="locked ? 'step5-approval-lock-line' : ((!disabled && !canDecideFinal) ? 'step5-approval-gate-note' : null)">
            <el-option label="Pending" value="Pending" />
            <el-option label="Rejected" value="Rejected" />
            <el-option label="Conditional" value="Conditional" />
            <el-option label="Approved" value="Approved" />
          </el-select>
          <field-cue :row="step" path="approvedStatus" label="Approval Status" layout="inline" dirty />
          <div v-if="!disabled && !canDecideFinal && !locked"><small class="text-muted" id="step5-approval-gate-note"><i class="fa fa-lock" aria-hidden="true"></i>&nbsp;{{gateNote}}</small></div>

        </el-form-item>

        <!-- Carlos, 2026-10-14: "Step 5 date field: hide it." until there is a decision.
             Undecided = no status, a blank one, or Pending in any case (XOL-593 / the API's
             rule, approvalLock.isDecided). A decider who picks a status sees the Date at once
             (the select's v-model decides, not the server's answer). -->
        <el-form-item label="Date" v-if="decided">
          <el-date-picker value-format="yyyy-MM-dd" v-model="step.approved_at"
          :format="dateFormat"
          :disabled="disabled || !canDecideFinal || locked || frozen"
          @change="approvalDate()" />
          <field-cue :row="step" path="approved_at" label="Approval Date" layout="inline" dirty />
        </el-form-item>

        <!-- XOL-596: a status of null (never decided) is not a decision either; the
             read-only block ("No remarks") is for a decision someone made. -->
        <!-- Carlos, 2026-10-14: the inline form only wrapped these onto their own rows
             because the editor is wide; the short read-only block (locked / completed)
             floated beside Approval and Date. `step5-full-row` keeps the same rows in
             every state. -->
        <!-- XOL-596 (readable remarks): the item's label became a real heading INSIDE it, the
             same element above the editor and the read-only block. -->
        <el-form-item class="step5-full-row" v-if="!isPending && (canDecideFinal || !undecided)">
          <h5 class="remarks-heading">Approval Remarks</h5>
          <!-- Locked, or the step is complete (XOL-596), or a viewer who cannot write it (XOL-608:
               not a final approver, view-only, a read-only drawer): the read-only block below. The
               editor renders only when it would be enabled. -->
          <template v-if="!approvalRemarksReadOnly">
            <html-input
            v-model="step.remarks"  :disabled="disabled"
            @focus="holdField(step, 'remarks')" @blur="releaseField(step, 'remarks')" @input="fieldChanged(step, 'remarks')" />
            <field-cue :row="step" path="remarks" label="Approval Remarks" layout="block" />
          </template>
          <read-only-remarks v-else aria-label="Approval Remarks" :html="step.remarks" />
        </el-form-item>

        <el-form-item class="step5-full-row" v-if="!disabled && !isPending && canDecideFinal" style="padding-top: 3em;">
          <el-button :type="approvalButton" icon="fa fa-check" @click="sendApproval()"
          :disabled="(disabled || !step.approvedStatus || step.approvedStatus === 'Pending')">
          Send {{step.approvedStatus}} e-Mail
        </el-button>
      </el-form-item>

      <el-row>
        <el-form :inline="true">
          <el-form-item label="Deposit Returned">
            <el-switch active-text="Yes" inactive-text="No" v-model="step5.depositReturned"  :disabled="fieldsDisabled" @change="fieldChanged(actionData, 'state.step5.depositReturned')" />
            <field-cue :row="actionData" path="state.step5.depositReturned" label="Deposit Returned" layout="inline" />
          </el-form-item>

          <el-form-item label="Amount" v-if="step5.depositReturned">
            <el-input type="number" v-model="step5.amount"
            :disabled="fieldsDisabled || !step5.depositReturned"
            @focus="holdField(actionData, 'state.step5.amount')" @blur="releaseField(actionData, 'state.step5.amount')" @input="fieldChanged(actionData, 'state.step5.amount')">
            <template #prepend>{{currencyLabel}}</template>
          </el-input>
          <field-cue :row="actionData" path="state.step5.amount" label="Deposit Returned amount" layout="inline" />
        </el-form-item>
      </el-form>
      </el-row>

      <el-form-item v-if="step5.deposit > 0" label="Deposit Returned">
        <strong v-text="this.formatMoney(step5.deposit, 0)"></strong>
      </el-form-item>

      <div v-if="!(step5.approved && (step.approvedStatus === 'Approved' || step.approvedStatus === 'Pending'))">
        <!-- XOL-534 (reviewer R1): the Tower Co reads the same shared
             column and must see who last changed it too. -->
        <!-- Carlos, 2026-10-15: Re-submission Remarks is written only by the RTB counterparty, so
             it is part of the form only on an RTB (or when a row already carries text). -->
        <read-only-remarks v-if="resubmitRemarksInForm" class="step5-full-row" label="Re-submission Remarks" :html="step5.resubmitRemarks"
          :stamp="resubmitRemarksStamp ? lastUpdatedLine(resubmitRemarksStamp) : null" />
      </div>

    </el-form>

  </section>

  <!-- Client view from here on down -->
  <section v-else>

    <el-form label-position="left" label-width="132px">

          <el-form-item label="Approval Status" v-if="step5p1">
            <h2>{{step5p1.approvedStatus ? step5p1.approvedStatus : 'Pending' }}
            &nbsp;
            <span v-if="!step5p1.approved">Approval</span>
            &nbsp;
            <i v-if="step5p1.approved" class="fa fa-check-circle text-success"></i>
            <i v-if="step5p1.approvedStatus === 'Conditional'" class="fa fa-question-circle text-warning"></i>
            <i v-if="step5p1.approvedStatus === 'Rejected'" class="fa fa-exclamation-circle text-danger"></i>
            <i v-if="!step5p1.approved && (step5p1.approvedStatus !== 'Rejected' && step5p1.approvedStatus !== 'Conditional')" class="fa fa-info-circle text-muted"></i>
            &nbsp;</h2>
            <i v-if="step5p1.approved && step5p1.approvedStatus === 'Approved' && decisionConfirmed(step5p1)" :title="approvalWhenTitle(step5p1)">{{approvalWhen(step5p1)}}</i>
          </el-form-item>

          <!-- XOL-596 (readable remarks): hidden when there is no text (an emptied quill value
               is not text), no longer squeezed into 41.7vw beside a 132px label. -->
          <read-only-remarks v-if="step5p1 && hasRemarksText(step5p1.remarks)" label="Approval Remarks" :html="step5p1.remarks" />


    <div v-if="!(step5p1 && step5p1.approved && (step5p1.approvedStatus === 'Pending' || step5p1.approvedStatus === 'Approved'))">

      <section v-if="resubmitRemarksInForm" class="remarks-section">
        <h5 class="remarks-heading">Re-submission Remarks</h5>
        <!-- XOL-526 (D5, S5): was `isTelco()` / `disabled` (canWriteStep()-
             gated, always false for a Telco) — a live-looking editor that
             silently discarded every save. canWriteOwnRequest() now offers a
             real write to the owner and the provider on an active RTB. -->
        <!-- XOL-596 (completed steps): once step 5 is complete the editor is the read-only
             block below, which was not gated on completion before, on either side. -->
        <!-- XOL-608: also when the drawer is read-only (the editor's own `:disabled` term). -->
        <template v-if="!resubmitRemarksReadOnly">
            <small v-if="resubmitRemarksNote" class="text-muted">{{resubmitRemarksNote}}</small>
            <!-- `no-form`: without it HtmlInput renders an el-form-item that this form's
                 label-width (132px) would indent under a heading that sits above. -->
            <html-input v-model="step5.resubmitRemarks" :disabled="!canWriteOwnRequest() || readOnly" no-form
              @focus="holdField(actionData, 'state.step5.resubmitRemarks')" @blur="releaseField(actionData, 'state.step5.resubmitRemarks')" @input="fieldChanged(actionData, 'state.step5.resubmitRemarks')" />
            <field-cue :row="actionData" path="state.step5.resubmitRemarks" label="Re-submission Remarks" layout="block" />
            <small v-if="resubmitRemarksStamp" class="remarks-stamp">{{lastUpdatedLine(resubmitRemarksStamp)}}</small>
        </template>
        <read-only-remarks v-else aria-label="Re-submission Remarks" :html="step5.resubmitRemarks"
          :stamp="resubmitRemarksStamp ? lastUpdatedLine(resubmitRemarksStamp) : null" />
      </section>

      </div>
    </el-form>
  </section>

  </div>

</div>

</template>



<script>

import {settings} from '@/components/Data'

import RequestMixins from '@/mixins/RequestMixins'

import FileDownload from '@/components/atoms/files/FileDownload'
import FileUpload from '@/components/atoms/files/FileUpload'

import ApprovalRemarks from '@/components/requests/workflow/ApprovalRemarks'
import FieldCue from '@/components/requests/FieldCue'
import ApprovalLockLine from '@/components/requests/ApprovalLockLine'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'

import { copyText } from '@/lib/requestSyncCopy'
import { isDecided } from '@/lib/approvalLock'

export default {

  // XOL-534 (reviewer): DateMixins already arrives transitively via
  // RequestMixins -> SiteMixins — a direct import here was redundant.
  mixins: [RequestMixins],

  components: {
    ApprovalLockLine,
    ApprovalRemarks,
    FieldCue,
    FileDownload,
    FileUpload,
    ReadOnlyRemarks
  },


  props: {

    rowData: {
      type: Object,
      required: true
    },

    rowIndex: {
      type: Number,
      default: null
    },

    stepData: {
      type: Object,
      default: null
    },

    readOnly: {
      type: Boolean,
      default: true
    }

  },



  methods: {

    // XOL-596: the fresh GET on open is adopted IN PLACE into the one object for
    // the row (F4), with the seq of when it was issued.
    loadOwnStep () {
      this.loadStep(this.stepData.id, this.step)
    },

    // XOL-596 (decision fields, Carlos 2026-10-14): the final approval's status and
    // date are FIELDS. A change marks the field changed and restarts the form's pause;
    // at the pause ONE approval command goes out, `POST /step/{id}/approval`, with
    // `expectedApprovedStatus` = the status the user SAW (the field's base, never the
    // v-model, which has already changed). The server sets the step's approved /
    // approved_at / approved_by AND the action's approved_at / approved_by /
    // approvalType (contract A9: Conditional is `approved` 0, and Pending clears
    // approvalType); the adopt of the response shows them. Nothing is written locally.
    // XOL-596 UX 15: only `canDecideFinal` may do it.
    approval () {
      return this.decisionChanged(this.step, 'approvedStatus')
    },

    // The approver back-dated the decision. Clearing the picker puts the date back.
    approvalDate () {
      if (!this.step.approved_at) {
        return this.decisionDateCleared(this.step)
      }

      return this.decisionChanged(this.step, 'approved_at')
    },


    sendApproval () {
      // XOL-582 / XOL-596 ordering rule: the remarks are saved BEFORE they are
      // e-mailed (the form pause is cut short and the changed fields flushed), and
      // the e-mail is not sent if one of those saves failed (its own notice says so).
      var self = this

      if (!this.canDecideFinal) {
        return Promise.resolve()
      }

      return this.flushBeforeSend([this.step, this.actionData]).then(function (ok) {
        if (ok) {
          self.sendApprovalMail()
        }
      })
    },

    sendApprovalMail () {
      this.approvalAlert(this.rtxInfo,
        this.step.approvedStatus,
        this.formatDate(this.step.approved_at),
        this.step.remarks, this.step2.siteId)
    }

  },



  watch: {

    // The editors unmount when the step freezes, and an unmounting quill gets no blur; the
    // amount input is disabled in place. Let go of the text paths they hold.
    frozen (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
        this.releaseField(this.actionData, 'state.step5.amount')
        this.releaseField(this.actionData, 'state.step5.resubmitRemarks')
      }
    },

    // The remarks editor unmounts when the approval locks (an adopt), and an unmounting quill
    // gets no blur: let go of the path it holds (F-L1, 2026-10-15).
    locked (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    // XOL-608: the editors also unmount when the viewer can no longer write them (the request
    // closes, a view-only flag arrives), and an unmounting quill gets no blur.
    approvalRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    resubmitRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.actionData, 'state.step5.resubmitRemarks')
      }
    }

  },


  data () {

    return {
      currencyLabel: settings.currency,
      dateFormat: settings.selectDateFormat,
      actionData: this.rowData,
      step: this.stepData,
      step2: this.rowData.state.step2,
      step3: this.rowData.state.step3,
      step5: this.rowData.state.step5,
      legalApproved: (this.rowData.status === 'Closed') ? true : this.rowData.state.step3.legalApproved,
      step5p1: null,
      gateNote: copyText('requestSync.step5Gate.note'),
      rtxInfo: this.info(this.rowData, this.stepData, 5)
    }

  }, // data


  computed: {

    // XOL-596 (UX 15): who may change the FINAL approval. The API checks nothing
    // beyond Tower Co, so this SPA gate is the only guard: before this, a
    // non-approver could change it by changing a select.
    canDecideFinal () {
      return (this.isApprover() || this.isManager()) && this.canAccess('project')
    },

    // XOL-596 (approval lock): the final approval, its date and its remarks.
    locked () {
      return this.approvalLocked(this.step)
    },

    // Is there a decision on screen? No status, a blank one and Pending (any case) are
    // not. The one rule (lib/approvalLock.isDecided) the API, the lock and XOL-593 use.
    decided () {
      return isDecided('step5', this.step)
    },

    // Never decided.
    undecided () {
      return !this.decided
    },

    disabled () {
      return !this.canWriteStep() || this.isViewOnly() || this.readOnly
    },

    // XOL-596 (completed steps): step 5 is complete, so everything in it is read-only for
    // every viewer until it is re-opened. A CONSTANT step number, from the server baseline.
    frozen () {
      return this.isStepFrozen(5)
    },

    // XOL-608: the Approval Remarks block shows exactly when the editor's own `:disabled`
    // (`disabled`) would be true, OR'd with the old terms: not a final approver, locked, or the
    // step is complete.
    approvalRemarksReadOnly () {
      return !this.canDecideFinal || this.locked || this.frozen || this.disabled
    },

    // XOL-608: the counterparty's Re-submission Remarks block shows unless the editor would be
    // enabled: `canWriteOwnRequest() && !isTowerCo() && !frozen`, and not `readOnly`
    // (XOL-534's "do NOT fold `!readOnly` into the v-if" is superseded: a disabled HtmlInput
    // rendered nothing when empty).
    resubmitRemarksReadOnly () {
      return !(this.canWriteOwnRequest() && !this.isTowerCo() && !this.frozen && !this.readOnly)
    },

    // Carlos, 2026-10-15: Re-submission Remarks (`state.step5.resubmitRemarks`) is written only
    // by the counterparties of an RTB; no surface writes it on an RTS, RTU or RTD. So it is part
    // of the form on an RTB, for every viewer; elsewhere only when a row already carries text
    // (shown read-only, so nothing stored is hidden).
    resubmitRemarksInForm () {
      var a = this.actionData || this.rowData

      return (!!a && String(a.actionType || '').toUpperCase() === 'RTB') || this.hasRemarksText(this.step5 && this.step5.resubmitRemarks)
    },

    // The deposit controls: `disabled`, or the step is complete.
    fieldsDisabled () {
      return this.disabled || this.frozen
    },

    // XOL-526/534 (UX §2.4, U-I): request-level `actions.state.step5.resubmitRemarks`
    // — a single shared column read by every party, so the "Last updated by"
    // line is shown to the Tower Co too (in its own `v-if="isTowerCo()"`
    // section above) as well as both counterparty branches below. Only the
    // shared-field NOTE (`resubmitRemarksNote`, below) stays counterparty-only,
    // since the Tower Co never writes this field.
    resubmitRemarksStamp () {
      return this.editStamp(this.actionData.state, 'resubmitRemarks')
    },

    resubmitRemarksNote () {
      return (this.isCounterpartyWriter() && this.hasDistinctProvider() && !this.frozen)
        ? this.sharedFieldNote('edit these remarks')
        : null
    }

  },


  created () {
    if (this.step) {
      // To handle existing RTx without this flag.
      this.step.legalApproved = (this.actionData.status !== 'Closed' ? this.step.legalApproved : true)

      // XOL-526 (S5, D9): was `canWriteStep()` — a counterparty may now
      // write its own party-2 row too. (No step field of step5's is
      // actually counterparty-writable today, but findStep()'s freshness is
      // applied uniformly across step1-5.)
      if ((this.canWriteStep() || this.canWriteOwnStep(this.step)) && !this.readOnly) {
        this.loadOwnStep()
      }

    }

  },


  mounted () {

    if (this.step) {

      if (this.step.stepNo === 5 && this.step.partyNo === 1) {
        this.step5p1 = this.step
      } else {
        this.step5p1 = this.findWorkflowStep(5, 1, this.rowData.steps)
      }

    }

  },


  beforeDestroy () {
    // XOL-596: no save here (it sent the whole step and the whole action). The
    // fields of this step save at the form's pause or at a flush point; a text
    // field still focused gets no blur on unmount.
    this.releaseFocused()

    if (!this.readOnly && this.step) {
      this.$emit('updated', this.step)

      if (this.step.stepNo === 5 && this.step.partyNo === 1) {
        this.step5p1 = this.step
      }
    }

  }

}
</script>

<style scoped>
  /* Carlos, 2026-10-14: Approval Remarks and Send e-Mail always take their own
     row of the inline form, locked or not. */
  .el-form--inline .step5-full-row {
    display: block;
    width: 100%;
  }
  /* XOL-596 (readable remarks): the inline form's content box is `inline-block`, which would
     shrink-wrap the read-only frame to the text's width. */
  .el-form--inline .step5-full-row ::v-deep .el-form-item__content {
    display: block;
  }
</style>
